import logoSvg from '@/assets/logo.svg'
import chipSvg from '@/assets/icons/chip.svg'
import { getCardsTillStreet } from '@/helpers/cards'
import { SNK } from '@/helpers/helpers'
import { t } from '@/locales/i18n'
import store from '@/store'
import { sanitizeQuery } from '@/store/router/router'
import {
  aboutSolutionsUrl,
  aiGuidesUrl,
  analyzedHandsExplainedUrl,
  analyzeGuideUrl,
  analyzerUrl,
  betSizesUrl,
  breakdownTabUrl,
  bubbleFactorUrl,
  customSolutionsUrl,
  discordAccountPaymentsUrl,
  discordInviteLinkUrl,
  discordInviteUrl,
  donkBettingTooltipUrl,
  eqChartUrl,
  evCompareTooltipUrl,
  evCompareTooltipYouTubeUrl,
  evConverterBountiesUrl,
  evConverterIcmUrl,
  filterSubsetsUrl,
  geometricBettingUrl,
  getMostOfAnalyzerUrl,
  gtoWizardPrivacyUrl,
  gtoWizardTermsUrl,
  helpCenterUrl,
  howToBuildCustomSolutionsUrl,
  howToCalculateRaiseSizeUrl,
  howToEditRangesUrl,
  icmPkoUrl,
  icmPressureGuideUrl,
  measurePerformanceUrl,
  nodelockingHowToUseUrl,
  preflopAiUrl,
  raCompareCombosUrl,
  raCompareEqrUrl,
  raCompareEqUrl,
  raCompareEvUrl,
  rakesGuidUrl,
  rangeBuilderHowToUseUrl,
  rangesTabUrl,
  rngTrainerPreferencesUrl,
  savingLoadingGuidesUrl,
  solutionBrowserUrl,
  solutionStatusUrl,
  trainAgainstCustomSolutionsUrl,
  trainerDrillsUrl,
  trainerHowToUseUrl,
  trainerStatsUrl,
  trainerTipsTricksUrl,
  uploadsExplainedUrl,
  uploadStatusesExplainedUrl,
  whatIsPotOddsUrl,
} from '@gto/urls'

export type Namespace =
  | 'tra/alpha'
  | 'tra/beta'
  | 'tra/gamma'
  | 'tra/delta'
  | 'tra/replay'
  | 'tmp/primary'
  | 'tmp/secondary'
  | 'tmp/extra1'
  | 'tmp/extra2'
  | 'tmp/extra3'
  | ''

export interface LinkArgs {
  namespace?: Namespace
  pathName?: string
  customQuery?: Record<string, unknown>
  hasBoardFilter?: boolean
  hasTmpBoard?: boolean
}
export const links_getQuery = ({
  namespace,
  pathName = 'PracticeTrainer',
  customQuery = {},
  hasBoardFilter = false,
  hasTmpBoard = false,
}: LinkArgs) => {
  const query_gameformat = store.getters[SNK('query_gameformat', namespace)]
  const query_history = store.getters[SNK('query_history', namespace)]
  const query_board = store.getters[SNK('query_board', namespace)]
  const query_cussol = store.getters[SNK('query_cussol', namespace)]

  let query = {
    board: query_board,
    ...query_history,
    ...query_gameformat,
    ...query_cussol,
  }

  if (pathName === 'PracticeTrainer') {
    const query_board_filter = store.getters[SNK('query_board_filter', namespace)]
    const query_fullhand_setup = store.getters[SNK('query_fullhand_setup', 'tra/alpha')]
    query = {
      ...query_fullhand_setup,
      ...(hasBoardFilter ? query_board_filter : {}),
      ...query,
      fh_start_spot: 'custom_spot',
    }
  } else if (pathName === 'StudySolutions') {
    const last_spot = store.getters[SNK('last_spot', namespace)]

    let board = ''
    if (hasTmpBoard) {
      const query_board_tmp = store.getters[SNK('query_board_tmp', namespace)]
      board = getCardsTillStreet(query_board_tmp, last_spot?.street || 'preflop')
    }
    query = {
      ...query,
      ...(hasTmpBoard ? { board } : {}),
      soltab: store.getters.sw_tabs_study,
    }
  }

  query = sanitizeQuery({
    ...query,
    ...customQuery,
  })

  return query
}
export const links_getUrl = (args: LinkArgs) => {
  const path = {
    PracticeTrainer: '/practice/trainer/',
    StudySolutions: '/solutions/',
  }[args.pathName || 'PracticeTrainer']

  const params = new URLSearchParams(links_getQuery(args))
  return `${window.location.origin}${path}?${params}`
}

export const getLinksAll = () => ({
  StudyStudy: {
    id: 'StudyStudy',
    pathName: 'StudySolutions',
    title: t('links.study_dashboard'),
    sub: t('links.study_solutions_sub'),
    icon: 'mdi-school',
    clr_main: 'var(--clr-primary)',
    // clr_secondary: 'var(--clr-study)',
  },
  StudySolutions: {
    id: 'StudySolutions',
    pathName: 'StudySolutions',
    data_tst: 'StudySolutions_gwiz',
    title: t('links.study_dashboard'),
    sub: t('links.study_solutions_sub'),
    icon: 'mdi-school',
    clr_main: 'var(--clr-primary)',
  },
  StudyCustomSolutions: {
    id: 'StudyCustomSolutions',
    pathName: 'StudySolutions',
    title: t('links.study_custom_solutions'),
    sub: t('links.study_custom_solutions_sub'),
    icon: 'mdi-folder-plus',
    clr_main: 'var(--clr-primary)',
    query: {
      soltab: store.getters.sw_tabs_study === 'reports' ? 'strategy' : store.getters.sw_tabs_study,
      solution_type: 'custom',
      preflop_actions: '',
      flop_actions: '',
      turn_actions: '',
      river_actions: '',
    },
  },
  StudySolLibSolutions: {
    id: 'StudySolLibSolutions',
    pathName: 'StudySolutions',
    title: t('links.study_gwizlib_solutions'),
    sub: t('links.study_gwizlib_solutions_sub'),
    icon: 'mdi-bookshelf',
    clr_main: 'var(--clr-primary)',
    query: {
      soltab: store.getters.sw_tabs_study === 'reports' ? 'strategy' : store.getters.sw_tabs_study,
      solution_type: 'gwiz',
      preflop_actions: '',
      flop_actions: '',
      turn_actions: '',
      river_actions: '',
    },
  },
  StudyReports: {
    id: 'StudyReports',
    pathName: 'StudySolutions',
    title: t('links.study_reports'),
    sub: t('links.study_reports_sub'),
    icon: 'mdi-chart-box-outline',
    clr_main: 'var(--clr-primary)',
    // clr_secondary: 'var(--clr-reports)',
    query: {
      soltab: 'reports',
    },
  },
  StudyCustomReports: {
    id: 'StudyCustomReports',
    pathName: 'StudySolutions',
    title: t('links.study_custom_reports'),
    sub: t('links.study_custom_reports_sub'),
    icon: 'mdi-folder',
    clr_main: 'var(--clr-primary)',
    style: '--clr-tag-back: var(--clr-primary); --tag-top: 1.85rem; --tag-right: 5rem;',
    query: {
      soltab: 'reports',
      solution_type: 'custom',
      preflop_actions: '',
      flop_actions: '',
      turn_actions: '',
      river_actions: '',
    },
  },
  Practice: {
    id: 'Practice',
    pathName: 'Practice',
    title: t('links.practice'),
    icon: 'mdi-google-controller',
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-blue)',
  },
  PracticePlay: {
    pathName: 'PracticePlay',
    title: t('links.practice'),
    sub: t('links.practice'),
    icon: 'mdi-google-controller',
    clr_main: 'var(--clr-blue)',
  },
  PracticeTrainer: {
    pathName: 'PracticeTrainer',
    title: t('links.practice_trainer'),
    sub: t('links.practice_trainer_sub'),
    icon: 'mdi-google-controller',
    tag: t('common.live'),
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-blue)',
    // clr_secondary: 'var(--clr-trainer)',
  },
  PracticeRangeBuilder: {
    pathName: 'PracticeRangeBuilder',
    title: t('links.practice_range_builder'),
    sub: t('links.practice_range_builder_sub'),
    icon: 'mdi-table-edit',
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-yellow)',
    // clr_secondary: 'var(--clr-range-builder)',
  },
  PracticeDrills: {
    pathName: 'PracticeDrills',
    // query: { ffdf_drill_type: 'my_drills' },
    title: t('links.practice_drills'),
    sub: t('links.practice_drills_sub'),
    icon: 'mdi-target',
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-error)',
  },
  PracticeHands: {
    pathName: 'PracticeHands',
    title: t('links.practice_hands'),
    sub: t('links.practice_hands_sub'),
    icon: 'mdi-cards-playing',
    style: '--clr-tag-back: var(--clr-blue-1)',
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-orange)',
  },
  PracticeSessions: {
    pathName: 'PracticeSessions',
    title: t('links.practice_sessions'),
    sub: t('links.practice_sessions_sub'),
    icon: 'mdi-cards-variant',
    style: '--clr-tag-back: var(--clr-blue-1)',
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-mtt)',
  },
  PracticeStats: {
    pathName: 'PracticeStats',
    title: t('links.practice_stats'),
    sub: t('links.practice_stats_sub'),
    icon: 'mdi-finance',
    style: '--clr-tag-back: var(--clr-blue-1)',
    clr_main: 'var(--clr-blue)',
    clr_secondary: 'var(--clr-primary)',
    // clr_secondary: 'var(--clr-trainer-stats)',
  },

  Analyze: {
    pathName: 'Analyze',
    title: t('links.analyze'),
    icon: 'mdi-lightbulb-on',
    clr_main: 'var(--clr-yellow)',
  },
  AnalyzeHandsV4: {
    pathName: 'AnalyzeHandsV4',
    title: t('links.analyze_hands'),
    sub: t('links.analyze_hands_sub'),
    icon: 'mdi-cards-playing',
    clr_main: 'var(--clr-yellow)',
    tag: 'New',
    style: '--clr-tag-back: var(--clr-primary); --clr-tag: var(--clr-white-in-primary, white)',
  },

  AnalyzeFilesV4: {
    pathName: 'AnalyzeFilesV4',
    title: t('links.analyze_uploads'),
    sub: t('links.analyze_uploads_sub'),
    icon: 'mdi-upload',
    clr_main: 'var(--clr-yellow)',
    style: '--clr-tag-back: var(--clr-primary); --clr-tag: var(--clr-white-in-primary, white)',
  },
  AnalyzeHandsV3: {
    pathName: 'AnalyzeHandsV3',
    title: t('links.analyze_hands'),
    sub: t('links.analyze_hands_sub'),
    icon: 'mdi-cards-playing',
    tour: 'linkhands',
    clr_main: 'var(--clr-yellow)',
    ...(store.getters.user_has_flag('HANDS_V4') && { tag: 'Old' }),
    style: '--clr-tag-back: var(--clr-error); --clr-tag: var(--clr-white-0)',
  },
  GtoReports: {
    pathName: 'GtoReports',
    title: t('links.gto_reports'),
    sub: t('links.gto_reports_sub'),
    icon: 'mdi-chart-box-outline',
    clr_main: 'var(--clr-yellow)',
    tag: 'New',
    style: '--clr-tag-back: var(--clr-primary); --clr-tag: var(--clr-white-in-primary, white)',
  },

  // -- delete after rls
  AnalyzeOriginal: {
    pathName: 'AnalyzeOriginal',
    title: t('links.analyze'),
    icon: 'mdi-lightbulb-on',
    clr_main: 'var(--clr-yellow)',
  },
  UserAccount: {
    pathName: 'UserAccount',
    title: t('links.UserAccount'),
    sub: t('links.UserAccount_sub'),
    icon: 'mdi-account-circle',
    clr_main: 'var(--clr-primary)',
  },
  UserSubscription: {
    pathName: store.getters.user_has_flag('REACT_SUBSCRIPTION_OVERVIEW')
      ? 'UserSubscriptionsOverview'
      : 'UserSubscription',
    title: t('links.UserSubscription'),
    // sub: t('links.UserSubscription_sub'),
    icon: 'mdi-package-variant-closed',
    clr_main: 'var(--clr-primary)',
  },
  UserGameSettings: {
    pathName: 'UserGameSettings',
    title: t('links.HHanalyzer'),
    sub: t('links.HHanalyzer_sub'),
    clr_main: 'var(--clr-primary)',
    icon: 'mdi-cogs',
  },
  UserAppearance: {
    pathName: 'UserAppearance',
    title: t('links.UserAppearance'),
    sub: t('links.UserAppearance_sub'),
    icon: 'mdi-palette-outline',
    clr_main: 'var(--clr-orange)',
    // clr_secondary: 'var(--clr-appearance)',
  },
  UserHotkeys: {
    pathName: 'UserHotkeys',
    title: t('links.UserHotkeys'),
    // sub: t('links.UserHotkeys_sub'),
    icon: 'mdi-keyboard',
    clr_main: 'var(--clr-orange)',
  },
  UserLanguage: {
    pathName: 'UserLanguage',
    title: t('links.UserLanguage'),
    // sub: t('links.UserLanguage_sub'),
    icon: 'mdi-earth',
    g1_hide: true,
    clr_main: 'var(--clr-orange)',
  },
  UserCredits: {
    pathName: 'UserCredits',
    title: t('links.UserCredits'),
    // sub: t('links.UserCredits_sub'),
    icon_svg: chipSvg,
    clr_main: 'var(--clr-primary)',
  },

  Dashboard: {
    pathName: 'Dashboard',
    title: 'GTO Wizard',
    icon_svg: logoSvg,
  },
  Coaching: {
    pathName: 'Coaching',
    title: t('table_features.study.coachings'),
    sub: t('links.weekly_coachings_sub'),
    icon: 'mdi-human-male-board',
    clr_main: 'var(--clr-primary)',
    // clr_secondary: 'var(--clr-coaching)',
  },
  StudyPlansDashboard: {
    pathName: 'StudyPlansDashboard',
    title: t('links.training_plans_dashboard'),
    sub: t('links.training_plans_dashboard_sub'),
    icon: 'gw-icon-gwizstudyplans',
    g1_hide: true,
    clr_main: 'var(--clr-primary)',

    style: `
          --tag-height:1rem;--tag-size:0.8rem;
          --clr-tag-back: var(--clr-primary);--clr-tag: var(--clr-white-0);
          `,
    // tag: t('common.new'),
  },
  FairPlay: {
    pathName: '/fairplay',
    title: t('links.fairplay'),
    icon: 'mdi-handshake',
  },
  Play: {
    pathName: 'Play',
    title: t('links.play'),
    icon: 'mdi-cards-playing',
    clr_main: 'var(--clr-green)',
  },
  IcmCalculatorView: {
    pathName: '/icm-calculator',
    title: t('links.icm_calculator'),
    icon: 'mdi-calculator',
  },
  NotExistent: {
    pathName: 'NotExistent',
  },
  external_HelpCenter: {
    g1_hide: true,
    id: 'external_HelpCenter',
    // clr_main: 'var(--clr-mtt)',
    clr_main: 'var(--clr-orange)',
    title: t('common.help_center'),
    sub: t('common.help_center_sub'),
    href: 'https://help.gtowizard.com/',
    // icon: 'gw-icon-gwizhelp',
    icon: 'mdi-book-information-variant',
  },
  external_Deals: {
    g1_hide: true,
    id: 'external_Deals',
    clr_main: 'var(--clr-orange)',
    title: t('links.deals'),
    sub: t('links.deals_sub'),
    tag: t('common.new'),
    style: '--clr-tag-back: var(--clr-primary); --tag-top: 1.83rem; --tag-right: 10.9rem;',
    href: 'https://rbwizard.com/',
    icon: 'mdi-handshake-outline',
  },
})

export const getLinksUrlLinks = () => {
  const learnMore = t('common.learn_more')

  return {
    help_center: {
      icon: 'mdi-book-information-variant',
      url: helpCenterUrl,
      text: t('common.help_center'),
    },
    discord_invite: { url: discordInviteUrl },
    gto_wizard_terms: {
      url: gtoWizardTermsUrl,
    },
    gto_wizard_privacy: {
      url: gtoWizardPrivacyUrl,
      // StaticFooter:14
    },
    discord_invite_link: {
      // hardcoded in en.js
      url: discordInviteLinkUrl,
    },
    discord_accounts_payments: {
      // hardcoded in en.js / no usage elsewhere
      // NOTE: no usages except commented code!
      url: discordAccountPaymentsUrl,
    },
    analyze_guide: {
      url: analyzeGuideUrl,
      text: t('messages.see_analyze_guide'),
    },
    rakes_guide: {
      url: rakesGuidUrl,
      text: t('messages.rakes_guide'),
    },
    analyzed_hands_explained: {
      url: analyzedHandsExplainedUrl,
      text: t('messages.analyzed_hands_explained'),
    },
    get_most_of_analyzer: {
      url: getMostOfAnalyzerUrl,
      text: t('messages.get_most_of_analyzer'),
    },
    uploads_explained: {
      url: uploadsExplainedUrl,
      text: t('messages.uploads_explained'),
    },
    upload_statuses_explained: {
      url: uploadStatusesExplainedUrl,
      text: t('messages.upload_statuses_explained'),
    },
    solution_browser: {
      url: solutionBrowserUrl,
      text: t('guides.solution_browser_guide'),
    },
    about_solutions: {
      url: aboutSolutionsUrl,
    },
    analyzer: {
      url: analyzerUrl,
    },
    filter_subsets: {
      url: filterSubsetsUrl,
    },
    trainer_tips_tricks: {
      url: trainerTipsTricksUrl,
    },
    trainer_how_to_use: {
      url: trainerHowToUseUrl,
    },
    rangebuilder_how_to_use: {
      url: rangeBuilderHowToUseUrl,
    },
    trainer_stats: {
      url: trainerStatsUrl,
    },
    trainer_drills: {
      url: trainerDrillsUrl,
    },
    ranges_tab: {
      url: rangesTabUrl,
    },
    breakdown_tab: {
      url: breakdownTabUrl,
    },
    eq_chart: {
      url: eqChartUrl,
    },
    ra_compare_combos: {
      url: raCompareCombosUrl,
    },
    ra_compare_ev: {
      url: raCompareEvUrl,
    },
    ra_compare_eq: {
      url: raCompareEqUrl,
    },
    ra_compare_eqr: {
      url: raCompareEqrUrl,
    },
    bubble_factor: {
      url: bubbleFactorUrl,
    },
    icm_pko: {
      url: icmPkoUrl,
    },
    custom_solutions: {
      // on more places just hardcoded url: 'https://blog.gtowizard.com/introducing-gto-wizard-ai/',
      url: customSolutionsUrl,
      text: t('guides.custom_solutions_guide'),
    },
    ai_guides: {
      url: aiGuidesUrl,
      text: t('guides.ai_guides'),
    },
    how_to_build_custom_solutions: {
      url: howToBuildCustomSolutionsUrl,
      // text: t('guides.how_to_build_custom_solutions'),
    },
    preflop_ai: {
      url: preflopAiUrl,
      // text: t('guides.preflop_ai'),
    },
    how_to_edit_ranges: {
      url: howToEditRangesUrl,
      text: t('guides.how_to_edit_ranges'),
    },
    bet_sizes: {
      url: betSizesUrl,
      text: t('guides.bet_sizes'),
    },
    saving_loading_guides: {
      url: savingLoadingGuidesUrl,
      text: t('guides.saving_loading_guides'),
    },
    train_against_custom_solutions: {
      url: trainAgainstCustomSolutionsUrl,
      text: t('guides.train_against_custom_solutions'),
    },
    rng_trainer_preferences: {
      url: rngTrainerPreferencesUrl,
      text: t('common.learn_more'),
    },
    what_is_pot_odds: {
      url: whatIsPotOddsUrl,
      text: t('common.learn_more'),
    },
    icm_pressure_guide: {
      url: icmPressureGuideUrl,
    },
    ev_compare_tooltip: {
      url: evCompareTooltipUrl,
      youtube_url: evCompareTooltipYouTubeUrl,
      text: t('guides.ev_compare_tips_tricks'),
    },
    donk_betting_tooltip: {
      url: donkBettingTooltipUrl,
    },
    geometric_betting: {
      url: geometricBettingUrl,
    },
    learn_more_solfigr_sizes_tooltip: {
      url: howToCalculateRaiseSizeUrl,
    },
    nodelocking_how_it_works: {
      url: nodelockingHowToUseUrl,
      text: t('guides.nodelocking_how_it_works'),
    },
    ev_converter_bounties: {
      url: evConverterBountiesUrl,
      text: t('solfigr.icm.ev_converter.Dollar_EV.text0'),
    },
    ev_converter_icm: {
      url: evConverterIcmUrl,
      text: t('solfigr.icm.ev_converter.Dollar_EV.text1'),
    },
    total_ev_loss: {
      url: measurePerformanceUrl,
      text: learnMore,
    },
    player_winloss: {
      url: measurePerformanceUrl,
      text: learnMore,
    },
    avg_gto_score: {
      url: measurePerformanceUrl,
      text: learnMore,
    },
    total_ev_loss_as_pot: {
      url: measurePerformanceUrl,
      text: learnMore,
    },
    avg_frequency_difference: {
      url: measurePerformanceUrl,
      text: learnMore,
    },
    hand_correctness: {
      url: measurePerformanceUrl,
      text: learnMore,
    },
    solution_status: {
      url: solutionStatusUrl,
      text: learnMore,
    },
  }
}
